import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { P, rows, type ID, type SalaryStructure, type SalaryStructureComponent } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { humanize } from '../../../lib/date';
import { fieldErrors } from '../../../lib/errors';
import { useMut } from '../../../lib/query';
import { Alert, Button, Card, Columns, ConfirmDialog, EmptyState, IconButton, ListItem, Loading, PageHeader, Row, Screen, Select, Stack, Switch, Text, TextField, useTheme } from '../../../ui';
import { BreakdownTable, type BreakdownWithWarnings } from '../components/Breakdown';
import { clone, Mono, NumField } from '../components/common';

export function SalaryStructuresScreen() {
  const { can } = useAuth();
  const { colors } = useTheme();
  const manage = can(P.PAYROLL_MANAGE);
  const list = useQuery({ queryKey: ['payroll', 'structures'], queryFn: () => api.payroll.structures.list() });
  const comps = useQuery({ queryKey: ['payroll', 'components'], queryFn: () => api.payroll.components.list({ per_page: 500 }) });
  const allComps = rows(comps.data).sort((a, b) => a.sort_order - b.sort_order);
  const structures = rows(list.data);
  const [selId, setSelId] = useState<ID | 'new' | null>(null);
  const [s, setS] = useState<Partial<SalaryStructure> | null>(null);
  const [ctc, setCtc] = useState<number | null>(1200000);
  const [preview, setPreview] = useState<BreakdownWithWarnings | null>(null);
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [del, setDel] = useState(false);

  useEffect(() => {
    if (selId === null && structures.length) setSelId(structures[0].id);
  }, [structures, selId]);
  const one = useQuery({ queryKey: ['payroll', 'structure', selId], queryFn: () => api.payroll.structures.get(selId as ID), enabled: typeof selId === 'number' });
  useEffect(() => {
    if (selId === 'new') setS({ name: '', description: '', pf_ceiling_applied: true, components: [] });
    else if (one.data && one.data.id === selId) setS(clone(one.data));
    else setS(null);
    setPreview(null);
    setErrs({});
  }, [selId, one.data]);

  const save = useMut(
    () => {
      const body = { ...s, components: (s?.components ?? []).map((c, i) => ({ pay_component_id: c.pay_component_id, formula: c.formula || null, sort_order: i })) };
      return s?.id ? api.payroll.structures.update(s.id, body) : api.payroll.structures.create(body);
    },
    { invalidate: [['payroll', 'structures'], ['payroll', 'structure']], success: 'Structure saved', onSuccess: (r) => setSelId(r.id), onError: (e) => setErrs(fieldErrors(e)) },
  );
  const runPreview = useMut(() => api.payroll.structures.preview(s!.id!, ctc ?? 0) as Promise<BreakdownWithWarnings>, { onSuccess: setPreview });
  const remove = useMut(() => api.payroll.structures.remove(s!.id!), {
    invalidate: [['payroll', 'structures']],
    success: 'Structure deleted',
    onSuccess: () => {
      setSelId(null);
      setDel(false);
    },
  });

  const cs = s?.components ?? [];
  const setCs = (c: SalaryStructureComponent[]) => setS({ ...s, components: c });
  const compOf = (id: ID) => allComps.find((c) => c.id === id);
  const unused = allComps.filter((c) => !cs.some((x) => x.pay_component_id === c.id));
  const move = (i: number, d: -1 | 1) => {
    const n = [...cs];
    [n[i + d], n[i]] = [n[i], n[i + d]];
    setCs(n);
  };

  const listCard = (
    <Card title="Structures" flush>
      {list.isLoading ? (
        <Loading />
      ) : !structures.length ? (
        <EmptyState icon="sliders" title="No structures" />
      ) : (
        <View>
          {structures.map((x, i) => (
            <ListItem key={x.id} title={x.name} subtitle={x.description} highlight={x.id === selId} divider={i < structures.length - 1} onPress={() => setSelId(x.id)} chevron />
          ))}
        </View>
      )}
    </Card>
  );

  const editor = !s ? (
    <Card>{selId && selId !== 'new' ? <Loading /> : <EmptyState icon="sliders" title="Select a structure" description="Or create a new one." />}</Card>
  ) : (
    <Stack>
      <Card
        title={s.id ? s.name : 'New structure'}
        actions={
          manage ? (
            <>
              {!!s.id && <IconButton icon="trash" label="Delete structure" onPress={() => setDel(true)} />}
              <Button variant="primary" title="Save" loading={save.isPending} onPress={() => save.mutate(undefined)} testID="save-structure" />
            </>
          ) : undefined
        }
      >
        <Stack gap={14}>
          <TextField label="Name" required value={s.name ?? ''} disabled={!manage} error={errs.name} onChangeText={(v) => setS({ ...s, name: v })} />
          <TextField label="Description" multiline value={s.description ?? ''} disabled={!manage} onChangeText={(v) => setS({ ...s, description: v })} />
          <Switch label="Apply PF wage ceiling" description="Restrict PF wage to the statutory ceiling (₹15,000)" value={!!s.pf_ceiling_applied} disabled={!manage} onChange={(v) => setS({ ...s, pf_ceiling_applied: v })} />
        </Stack>
      </Card>
      <Card title="Components" subtitle="Evaluated top to bottom. Leave the formula blank to use the component default.">
        <Stack gap={10} testID="structure-components">
          {!!errs.components && <Alert tone="danger">{errs.components}</Alert>}
          {!cs.length && <Text variant="muted">No components yet.</Text>}
          {cs.map((c, i) => {
            const pc = compOf(c.pay_component_id) ?? c.pay_component;
            return (
              <View key={c.pay_component_id} style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, paddingBottom: 10, borderBottomWidth: i < cs.length - 1 ? 1 : 0, borderBottomColor: colors.border }}>
                <Text variant="small" style={{ width: 20 }}>
                  {i + 1}
                </Text>
                <View style={{ flex: 1, minWidth: 150 }}>
                  <Text weight="semibold">
                    {pc?.name ?? `#${c.pay_component_id}`} <Mono>{pc?.code}</Mono>
                  </Text>
                  <Text variant="small">
                    {humanize(pc?.type)} · {humanize(pc?.calculation)}
                  </Text>
                </View>
                <TextField
                  accessibilityLabel={`Formula for ${pc?.code ?? ''}`}
                  placeholder={pc?.formula ?? (pc?.calculation === 'balancing' ? 'Balancing' : 'Default')}
                  value={c.formula ?? ''}
                  disabled={!manage}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                  containerStyle={{ flex: 1.4, minWidth: 180 }}
                  onChangeText={(v) => setCs(cs.map((x, j) => (j === i ? { ...x, formula: v } : x)))}
                />
                {manage && (
                  <Row gap={2} wrap={false}>
                    <IconButton size="sm" icon="chevronUp" label={`Move ${pc?.code ?? ''} up`} onPress={() => (i > 0 ? move(i, -1) : undefined)} />
                    <IconButton size="sm" icon="chevronDown" label={`Move ${pc?.code ?? ''} down`} onPress={() => (i < cs.length - 1 ? move(i, 1) : undefined)} />
                    <IconButton size="sm" icon="x" label={`Remove ${pc?.code ?? ''}`} onPress={() => setCs(cs.filter((_, j) => j !== i))} />
                  </Row>
                )}
              </View>
            );
          })}
          {manage && unused.length > 0 && (
            <View style={{ maxWidth: 360 }}>
              <Select
                value={null}
                placeholder="+ Add component…"
                options={unused.map((c) => ({ value: c.id, label: `${c.name} (${c.code})`, description: humanize(c.type) }))}
                onChange={(v) => setCs([...cs, { pay_component_id: v, formula: null, sort_order: cs.length }])}
                testID="add-structure-component"
              />
            </View>
          )}
        </Stack>
      </Card>
      <Card title="CTC preview">
        <Stack gap={12}>
          <Row align="flex-end">
            <NumField label="Annual CTC (₹)" value={ctc} onChange={setCtc} allowEmpty width={200} testID="preview-ctc" />
            <Button icon="calc" title="Preview" style={{ alignSelf: 'flex-end' }} disabled={!s.id || !ctc} loading={runPreview.isPending} onPress={() => runPreview.mutate(undefined)} testID="preview-structure" />
            {!s.id && <Text variant="small">Save the structure first.</Text>}
          </Row>
          {preview && <BreakdownTable b={preview} testID="structure-preview" />}
        </Stack>
      </Card>
    </Stack>
  );

  return (
    <Screen refreshing={list.isRefetching} onRefresh={() => list.refetch()} testID="salary-structures">
      <PageHeader
        title="Salary structures"
        subtitle="Templates that split CTC into components. Assign to employees or grades."
        actions={manage ? <Button variant="primary" icon="plus" title="New structure" onPress={() => setSelId('new')} testID="new-structure" /> : undefined}
      />
      <Columns ratio={[1, 3]}>
        {listCard}
        {editor}
      </Columns>
      <ConfirmDialog open={del} onClose={() => setDel(false)} onConfirm={() => remove.mutate(undefined)} loading={remove.isPending} title="Delete structure?" message="Employees already assigned keep their salary history." danger confirmLabel="Delete" />
    </Screen>
  );
}
